import { attr, belongsTo } from "@ember-data/model";
import { getOwner } from "@ember/owner";
import dayjs from "dayjs";
import {
  validateDate,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import type SurveyRequestedJournalEvent from "parog-web/models/survey-requested-journal-event";
import tWithDefault from "parog-web/utils/t-with-default";

//---- Automatically generated, don't update this part ----

/**
 * This event is used when a Patient completes a survey/form. It contains the raw
 * answers of the Patient, and is linked to a form definition through either the
 * `form-id` attribute or the survey-requested-journal-event relationship (which
 * has a `form-id`).
 */
export default class SurveyCompletedJournalEvent extends JournalEvent {
  static type = "survey-completed-journal-event";

  @attr("string") declare formId: string;

  @attr({
    defaultValue() {
      return {};
    }
  })
  declare rawInputs: any;

  @attr() declare linkedJournalEventIds: number[];

  @belongsTo("survey-requested-journal-event", {
    async: false,
    inverse: "surveyCompletedJournalEvent"
  })
  declare surveyRequestedJournalEvent: SurveyRequestedJournalEvent;

  static validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.survey-completed-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ]
    };
  }

  //---- End of generated code ----

  @belongsTo("patient", {
    async: false,
    inverse: "surveyCompletedJournalEvents"
  })
  declare patient: Patient;

  get completedFormId(): string | undefined {
    if (this.formId) {
      return this.formId;
    } else if (this.surveyRequestedJournalEvent) {
      return this.surveyRequestedJournalEvent.formId;
    }

    return undefined;
  }

  get summary() {
    if (this.completedFormId && this.completedFormId.includes("/")) {
      // This is a ForcIdentifier
      const parts = this.completedFormId.split("/");
      return Object.assign({}, super.summary, {
        forcIdentifier: {
          feature: parts[0],
          component: parts[1]
        }
      });
    }

    return Object.assign({}, super.summary, {
      formId: this.completedFormId
    });
  }

  get description(): string | undefined {
    const owner = getOwner(this) ?? {};

    if (this.completedFormId) {
      const forcComponents = owner["forcComponents"];
      if (!forcComponents) return;

      const componentClass = forcComponents;
      if (!componentClass) return;

      const displayName = componentClass["forcDisplayName"];
      if (displayName) return displayName;
    }

    console.error(`no form id found for ${this}`, this);
    return undefined;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "survey-completed-journal-event": SurveyCompletedJournalEvent;
  }
}
